/**
 * MONTHLY INSPECTIONS, AS THE PARTNER APP READS THEM.
 *
 * Two functions serve this app, both defined in fleet-backend's
 * `20261015010000_a_monthly_inspection_says_what_must_be_fixed.sql`:
 *
 *   get_inspection_board(p_partner_id)       one row per contract on the partner's cars, plus the
 *                                            held cars with no contract in progress; it feeds the
 *                                            chip on each contract row.
 *   get_contract_inspections(p_contract_id)  one contract's car, in the CAR's view for a partner:
 *                                            every visit on the car, on all its contracts and
 *                                            between them; it feeds the inspections page.
 *
 * THE SERVER DERIVES, THIS FILE ONLY NAMES. Attendance, the late flag, each visit's result, the
 * car's condition and the hold are all worked out by those functions from the stored lines. Nothing
 * here recomputes one of them: the chip rule and the status card pick which server field to show
 * and put it into words.
 *
 * The types are hand-written because this app has no generated `types.ts` (the client in
 * `src/integrations/supabase/client.ts` is untyped). They follow the functions' `jsonb_build_object`
 * keys one for one, and they deliberately leave out what this app must never render: the board's
 * `driver_phone` and `has_login` (null for a partner caller anyway), the driver's name, and each
 * visit's `remarks`. A partner sees the car, not the driver's details.
 *
 * `remarks` is the visit's free text as an admin typed it (`vehicle_inspections.description`). No
 * capture chip shapes it, the 25 legacy rows' notes date from the months when the signed bodies
 * still carried the inspection fee, and plan section 8 does not list it on the partner's
 * certificate. So it is not typed here and nothing can render it, until the owner rules it shows.
 *
 * NO MONEY TEXT. Nothing in this file, and nothing built from it, names an amount, a fee or a
 * payment (ruled 2026-10-15).
 *
 * Dates arrive as `YYYY-MM-DD` and are read through `parseBusinessDay`, field by field, so a
 * viewer west of Greenwich never sees the day before: `new Date('2026-11-03')` is UTC midnight,
 * which is 2 Nov in New York.
 */

import { format } from 'date-fns';
import { parseBusinessDay } from '@/lib/format';

// ─── Codes ───────────────────────────────────────────────────────────────────

/** `YYYY-MM-DD`, a Ghana calendar day. */
export type Ymd = string;

export type InspectionAttendance = 'not_owed' | 'not_yet' | 'presented' | 'held' | 'missed';
export type InspectionCondition = 'clean' | 'fix_soon' | 'held';
export type InspectionResult = 'passed' | 'passed_fix_soon' | 'held' | 'notes_only';
export type InspectionGrade = 'ok' | 'fix_soon' | 'fix_now' | 'fixed' | 'not_needed';
export type InspectionKind = 'monthly' | 'recheck';
export type InspectionSlot =
    | 'front_34'
    | 'driver_side'
    | 'rear_34'
    | 'passenger_side'
    | 'dashboard'
    | 'interior';
export type InspectionBoardChip = 'held' | 'missed' | 'not_yet' | 'done' | 'not_owed';

// ─── get_inspection_board ────────────────────────────────────────────────────

/**
 * One row of the board's `rows[]`: a contract on one of the partner's cars that owes the round or
 * is in progress. The board also returns `driver_id`, `driver_name`, `driver_phone` and
 * `has_login`; the last two are null for a partner, and none of the four is read here.
 */
export interface InspectionBoardRow {
    contract_id: number;
    vehicle_id: number;
    plate: string | null;
    make: string | null;
    model: string | null;
    /** NULL before the first round. */
    attendance: InspectionAttendance | null;
    /** Set only when attendance is `presented`. */
    late: boolean | null;
    /** The car's condition today. */
    condition: InspectionCondition;
    chip: InspectionBoardChip | null;
    /** The round's monthly on this contract: the first one dated inside the round's span. */
    inspection_id: number | null;
    inspected_on: Ymd | null;
    result: Exclude<InspectionResult, 'notes_only'> | null;
    open_fix_now: number;
    open_fix_soon: number;
    odometer_km: number | null;
    previous_odometer_km: number | null;
    last_service_km: number | null;
}

/** A car with no contract in progress today on which some line's latest grade is Must fix. */
export interface InspectionHeldWithoutContract {
    vehicle_id: number;
    plate: string | null;
    make: string | null;
    model: string | null;
    open_fix_now: number;
    open_fix_soon: number;
    open_items: number;
    earliest_raised_on: Ymd | null;
}

export interface InspectionBoard {
    /** The round, as the first of its month; NULL before the first round. */
    period: Ymd | null;
    opens_on: Ymd | null;
    closes_on: Ymd | null;
    today: Ymd;
    counts: {
        held: number;
        missed: number;
        not_yet: number;
        done: number;
        not_owed: number;
        held_without_contract: number;
    };
    rows: InspectionBoardRow[];
    held_without_contract: InspectionHeldWithoutContract[];
}

// ─── get_contract_inspections ────────────────────────────────────────────────

export interface InspectionChecklistSection {
    key: string;
    label: string;
    position: number;
}

export interface InspectionChecklistLine {
    key: string;
    section: string;
    label: string;
    guidance: string;
    position: number;
    added_on: Ymd;
}

export interface InspectionChecklist {
    sections: InspectionChecklistSection[];
    lines: InspectionChecklistLine[];
}

export interface InspectionOpenItemPhoto {
    id: number;
    url: string;
    line_id: number;
    inspection_id: number;
}

/** An item whose latest line on the car is open (Fix soon or Must fix). Must fix first. */
export interface InspectionOpenItem {
    item: string;
    grade: Extract<InspectionGrade, 'fix_soon' | 'fix_now'>;
    note: string | null;
    line_id: number;
    inspection_id: number;
    raised_on: Ymd;
    /** The open run's length minus one. */
    times_carried: number;
    /** The photos of every line in the open run. */
    photos: InspectionOpenItemPhoto[];
    /** Present for a partner: the run began on another contract, or between contracts. */
    raised_before_contract?: boolean;
}

/** This contract's place in the round whose span holds today. */
export interface InspectionCurrent {
    period: Ymd | null;
    opens_on: Ymd | null;
    closes_on: Ymd | null;
    today: Ymd;
    /** NULL before the first round, or when the contract is in no round. */
    attendance: InspectionAttendance | null;
    late: boolean | null;
    /** The car's condition (a partner always gets it). */
    condition: InspectionCondition | null;
    open_fix_now: number | null;
    open_fix_soon: number | null;
    inspection_id: number | null;
    inspected_on: Ymd | null;
    result: InspectionResult | null;
    released_inspection_id: number | null;
    released_on: Ymd | null;
    next_period: Ymd | null;
    next_opens_on: Ymd | null;
    next_closes_on: Ymd | null;
}

export interface InspectionVisitLine {
    id: number;
    item: string;
    grade: InspectionGrade;
    note: string | null;
    previous_grade: InspectionGrade | null;
}

export interface InspectionVisitPhoto {
    id: number;
    url: string;
    slot: InspectionSlot | null;
    line_id: number | null;
}

export interface InspectionVisit {
    id: number;
    /** NULL for a legacy, notes-only row. */
    kind: InspectionKind | null;
    contract_id: number | null;
    between_contracts: boolean;
    inspection_at: string;
    inspected_on: Ymd;
    /** Monthlies only: the round the visit counts for. */
    period: Ymd | null;
    late: boolean | null;
    result: InspectionResult;
    released: boolean | null;
    odometer_km: number | null;
    previous_odometer_km: number | null;
    odometer_change_km: number | null;
    last_service_km: number | null;
    // The function also returns `remarks`; left out on purpose (see the header).
    inspector_first_name: string | null;
    lines: InspectionVisitLine[];
    photos: InspectionVisitPhoto[];
}

export interface ContractInspections {
    contract_id: number;
    viewer: 'admin' | 'partner' | 'driver';
    /** The function also returns the driver's id and name here; this app reads neither. */
    header: {
        vehicle_id: number | null;
        plate: string | null;
        make: string | null;
        model: string | null;
        roadworthy_expires_on: Ymd | null;
        insurance_expires_on: Ymd | null;
    };
    checklist: InspectionChecklist;
    open_items: InspectionOpenItem[];
    current: InspectionCurrent;
    /** Newest first. */
    visits: InspectionVisit[];
}

// ─── Labels ──────────────────────────────────────────────────────────────────

export const GRADE_LABELS: Record<InspectionGrade, string> = {
    ok: 'OK',
    fix_soon: 'Fix soon',
    fix_now: 'Must fix',
    fixed: 'Fixed',
    not_needed: 'No fix needed',
};

export const RESULT_LABELS: Record<InspectionResult, string> = {
    passed: 'Passed',
    passed_fix_soon: 'Passed – fix soon',
    held: 'Held',
    notes_only: 'Notes only',
};

export const CONDITION_LABELS: Record<InspectionCondition, string> = {
    clean: 'Clean',
    fix_soon: 'Fix soon',
    held: 'Held',
};

/** The six angle shots, in walk-round order. */
export const SLOT_ORDER: InspectionSlot[] = [
    'front_34',
    'driver_side',
    'rear_34',
    'passenger_side',
    'dashboard',
    'interior',
];

export const SLOT_LABELS: Record<InspectionSlot, string> = {
    front_34: 'Front 3/4',
    driver_side: 'Driver side',
    rear_34: 'Rear 3/4',
    passenger_side: 'Passenger side',
    dashboard: 'Dashboard',
    interior: 'Interior',
};

export type InspectionTone = 'danger' | 'warning' | 'success' | 'info' | 'neutral';

/** Badge classes per tone, in the house palette of `STAGE_BADGE_STYLES`. */
export const INSPECTION_TONE_STYLES: Record<InspectionTone, string> = {
    danger: 'border-red-200 bg-red-50 text-red-700 dark:border-red-800 dark:bg-red-950/40 dark:text-red-400',
    warning: 'border-amber-200 bg-amber-50 text-amber-700 dark:border-amber-800 dark:bg-amber-950/40 dark:text-amber-400',
    success: 'border-emerald-200 bg-emerald-50 text-emerald-700 dark:border-emerald-800 dark:bg-emerald-950/40 dark:text-emerald-400',
    info: 'border-blue-200 bg-blue-50 text-blue-700 dark:border-blue-800 dark:bg-blue-950/40 dark:text-blue-400',
    neutral: 'border-slate-200 bg-slate-50 text-slate-600 dark:border-slate-700 dark:bg-slate-800/40 dark:text-slate-400',
};

export const GRADE_TONES: Record<InspectionGrade, InspectionTone> = {
    ok: 'neutral',
    fix_soon: 'warning',
    fix_now: 'danger',
    fixed: 'success',
    not_needed: 'neutral',
};

export const RESULT_TONES: Record<InspectionResult, InspectionTone> = {
    passed: 'success',
    passed_fix_soon: 'warning',
    held: 'danger',
    notes_only: 'neutral',
};

export const CONDITION_TONES: Record<InspectionCondition, InspectionTone> = {
    clean: 'success',
    fix_soon: 'warning',
    held: 'danger',
};

/** The checklist line's label for a stored item key, or the key itself for one this build does not know. */
export function itemLabel(checklist: InspectionChecklist | null | undefined, item: string): string {
    return checklist?.lines.find(l => l.key === item)?.label ?? item.replace(/_/g, ' ');
}

// ─── Dates ───────────────────────────────────────────────────────────────────

/** '3 Nov': the calendar day a `YYYY-MM-DD` names, in every viewer zone. Null when unusable. */
export function shortDay(ymd: Ymd | null | undefined): string | null {
    const day = parseBusinessDay(ymd);
    return day ? format(day, 'd MMM') : null;
}

/** '3 Dec 2026': the same day with its year, for a visit in the history. Null when unusable. */
export function longDay(ymd: Ymd | null | undefined): string | null {
    const day = parseBusinessDay(ymd);
    return day ? format(day, 'd MMM yyyy') : null;
}

/** 'December': the month a round (`YYYY-MM-01`) names. Null when unusable. */
export function monthName(ymd: Ymd | null | undefined): string | null {
    const day = parseBusinessDay(ymd);
    return day ? format(day, 'MMMM') : null;
}

/**
 * '1–7 Dec' for a window inside one month, '28 Nov – 4 Dec' across two. The two days come from
 * the server (`inspection_window`); the client never works out a window's bounds itself.
 */
export function windowRange(opensOn: Ymd | null | undefined, closesOn: Ymd | null | undefined): string | null {
    const opens = parseBusinessDay(opensOn);
    const closes = parseBusinessDay(closesOn);
    if (!opens || !closes) return null;
    if (opens.getFullYear() === closes.getFullYear() && opens.getMonth() === closes.getMonth()) {
        return `${format(opens, 'd')}–${format(closes, 'd MMM')}`;
    }
    return `${format(opens, 'd MMM')} – ${format(closes, 'd MMM')}`;
}

/** '48,210 km'. */
export function formatKm(km: number | null | undefined): string | null {
    if (km === null || km === undefined || !Number.isFinite(km)) return null;
    return `${new Intl.NumberFormat('en').format(km)} km`;
}

/** '+1,240 km' / '-120 km' / '0 km'. */
export function formatKmChange(km: number | null | undefined): string | null {
    if (km === null || km === undefined || !Number.isFinite(km)) return null;
    const sign = km > 0 ? '+' : km < 0 ? '-' : '';
    return `${sign}${new Intl.NumberFormat('en').format(Math.abs(km))} km`;
}

// ─── The chip on a contract row ──────────────────────────────────────────────

export interface InspectionChipView {
    /** A stable key for tests: which branch of the rule chose the label. */
    key: 'held' | 'missed' | 'not_yet' | 'result' | 'open';
    label: string;
    tone: InspectionTone;
}

/** A contract row's chip, and the contract whose inspections page it opens. */
export interface InspectionEntry {
    chip: InspectionChipView;
    /** The contract the link opens; null when neither the list row nor the board names one. */
    contractId: number | null;
}

export interface InspectionBoardIndex {
    rowsByContract: Map<number, InspectionBoardRow>;
    /** Each car's first row in the board's own order, which puts the most pressing chip first. */
    rowsByVehicle: Map<number, InspectionBoardRow>;
    /** The board's `held_without_contract` group: cars held now with no contract in progress today. */
    heldWithoutContract: Set<number>;
}

/** Index the board once per fetch, so each list row looks its chip up rather than scanning. */
export function indexInspectionBoard(board: InspectionBoard | null | undefined): InspectionBoardIndex {
    const rowsByContract = new Map<number, InspectionBoardRow>();
    const rowsByVehicle = new Map<number, InspectionBoardRow>();
    for (const row of board?.rows ?? []) {
        rowsByContract.set(row.contract_id, row);
        if (!rowsByVehicle.has(row.vehicle_id)) rowsByVehicle.set(row.vehicle_id, row);
    }
    const heldWithoutContract = new Set((board?.held_without_contract ?? []).map(car => car.vehicle_id));
    return { rowsByContract, rowsByVehicle, heldWithoutContract };
}

/** The chip that names no state: the way into a car's inspections when the board has no label for it. */
export const OPEN_INSPECTIONS_CHIP: InspectionChipView = { key: 'open', label: 'Inspections', tone: 'neutral' };

/**
 * THE CHIP RULE (plan section 8, "The chip"), read from the board row's own `chip`, which the
 * function derives (held from the car's open Must fix lines, then the attendance):
 *
 *   held               Held: the hold belongs to the car, so every row on a held car carries it
 *   missed / not_yet   Missed / Not yet
 *   done / not_owed    the round's monthly, when the row has one: its result and its day
 *                      ('Passed 3 Dec')
 *   anything else      Inspections: a link that names no state
 *
 * A car the board has no row for still reads Held when it is in `held_without_contract`.
 *
 * WHICH BOARD ROW. The list row's contract is the car's latest by created_at
 * (get_partner_vehicle_pipeline); the board lists the contracts that owe the round or are in
 * progress. When the board has no row for the list row's contract (a draft created on a car whose
 * earlier contract is still in progress, say), the car's first row in the board's order stands in,
 * and the chip opens that row's contract.
 *
 * WHAT THE CHIP DOES NOT SAY until the owner rules, because the board returns no field for it or
 * the plan has no words for it:
 *   - a round's monthly whose result is Held, on a car no longer held. The board carries no
 *     release, "Held 3 Dec" would contradict the car today, and "Released" would be the client
 *     inferring one. The page names the release from `current.released_on`.
 *   - attendance `held` (kept at the yard in the window, since released), and `not_owed` with no
 *     monthly in the round.
 *   - the car's last result outside this round (a re-check, an earlier round's monthly): the plan
 *     asks for "the last result and its date", but the board returns only the round's monthly, so
 *     a chip that names it needs a car-level last result on the board rows, or an owner ruling that
 *     the chip names the round's monthly only.
 * Each of those gets the neutral link, as does every row before the first round and every ended
 * contract the board does not list, so no car with a contract is left without a way into its
 * inspections.
 */
export function inspectionEntry(
    index: InspectionBoardIndex,
    contractId: number | null | undefined,
    vehicleId: number,
): InspectionEntry | null {
    const listed = contractId === null || contractId === undefined ? undefined : index.rowsByContract.get(contractId);
    const row = listed ?? index.rowsByVehicle.get(vehicleId);
    const target = row?.contract_id ?? contractId ?? null;
    const chip = boardChip(index, row, vehicleId) ?? (target !== null ? OPEN_INSPECTIONS_CHIP : null);
    return chip ? { chip, contractId: target } : null;
}

const HELD_CHIP: InspectionChipView = { key: 'held', label: 'Held', tone: 'danger' };

function boardChip(
    index: InspectionBoardIndex,
    row: InspectionBoardRow | undefined,
    vehicleId: number,
): InspectionChipView | null {
    if (!row) return index.heldWithoutContract.has(vehicleId) ? HELD_CHIP : null;

    switch (row.chip) {
        case 'held':
            return HELD_CHIP;
        case 'missed':
            return { key: 'missed', label: 'Missed', tone: 'danger' };
        case 'not_yet':
            return { key: 'not_yet', label: 'Not yet', tone: 'warning' };
        case 'done':
        case 'not_owed': {
            const day = shortDay(row.inspected_on);
            if (row.result && row.result !== 'held' && day) {
                return { key: 'result', label: `${RESULT_LABELS[row.result]} ${day}`, tone: RESULT_TONES[row.result] };
            }
            return null;
        }
        default:
            return null;
    }
}

// ─── The status card on the inspections page ─────────────────────────────────

export interface InspectionStatusView {
    key: 'held' | 'missed' | 'not_yet' | 'kept' | 'presented' | 'not_owed' | 'not_started' | 'not_due';
    title: string;
    sentence: string;
    tone: InspectionTone;
}

/**
 * THE STATUS CARD (plan section 8, the partner's wording). The first state that applies, read from
 * `current` alone, and from the server's codes only: the card never turns one attendance into
 * another.
 *
 *   Held               the car's condition is held, whatever the attendance
 *   Missed             attendance missed
 *   Not yet            attendance not_yet
 *   Kept at the yard   attendance held (kept in the window, since released)
 *   Presented          attendance presented
 *   Due next round     attendance not_owed: names the next round's window. A contract that went
 *                      in progress during the round can still have a monthly in it; the facts
 *                      row under the card shows that visit
 *   Not started        before the first round: names the first window
 *   Not due            in a round, but the contract is in no round (not in progress)
 *
 * AWAITING THE OWNER'S WORDING. The plan's table has six rows. The title "Due next round" (its row
 * is "Not owed", which reads as money here), the last two states, title and sentence, are this
 * build's words; the page needs something to say before the first round opens and on a contract
 * that is not in progress. The Not owed sentence is the owner's (ruling 2026-10-16): it names the
 * contract where the plan's ("This car's first inspection is in January, 1–7 Jan.") named the car,
 * which is untrue on a car whose earlier visits are listed below it.
 */
export function inspectionStatus(current: InspectionCurrent): InspectionStatusView {
    const round = monthName(current.period);

    if (current.condition === 'held') {
        return {
            key: 'held',
            title: 'Held',
            sentence: 'This car is held at the yard until these are fixed; Primelane will release it after a re-check.',
            tone: 'danger',
        };
    }

    if (current.attendance === 'missed') {
        return {
            key: 'missed',
            title: 'Missed',
            sentence: `The driver did not present this car for the ${round} inspection.`,
            tone: 'danger',
        };
    }

    if (current.attendance === 'not_yet') {
        return {
            key: 'not_yet',
            title: 'Not yet',
            sentence: `The ${round} inspection is due ${windowRange(current.opens_on, current.closes_on)}. The driver has not presented this car yet.`,
            tone: 'warning',
        };
    }

    if (current.attendance === 'held') {
        return {
            key: 'kept',
            title: 'Kept at the yard',
            sentence: `Primelane kept this car at the yard during the ${round} window, so the driver did not have to present it.`,
            tone: 'info',
        };
    }

    if (current.attendance === 'presented') {
        return {
            key: 'presented',
            title: 'Presented',
            sentence: `Presented for the ${round} inspection on ${shortDay(current.inspected_on)}.`,
            tone: current.result ? RESULT_TONES[current.result] : 'success',
        };
    }

    const nextMonth = monthName(current.next_period);
    const nextWindow = windowRange(current.next_opens_on, current.next_closes_on);

    if (current.attendance === 'not_owed') {
        return {
            key: 'not_owed',
            title: 'Due next round',
            sentence: nextMonth && nextWindow
                ? `This contract's first inspection is in ${nextMonth}, ${nextWindow}.`
                : "This contract's first inspection is the next round.",
            tone: 'neutral',
        };
    }

    if (current.period === null) {
        return {
            key: 'not_started',
            title: 'Not started',
            sentence: nextMonth && nextWindow
                ? `Monthly inspections start with the ${nextMonth} round, ${nextWindow}.`
                : 'No inspection round is scheduled yet.',
            tone: 'neutral',
        };
    }

    return {
        key: 'not_due',
        title: 'Not due',
        sentence: 'This contract is not in progress, so no monthly inspection is due on it this round.',
        tone: 'neutral',
    };
}

/**
 * The release under the status: the visit in the round's span that took the car from held to not
 * held, as get_contract_inspections derives it (`released_inspection_id`, `released_on`). Null while
 * the car is held again, which the Held title above already says. Worded from that visit's kind: a
 * monthly that grades the Must fix Fixed releases the car without a re-check.
 */
export function releaseNote(
    current: Pick<InspectionCurrent, 'condition' | 'released_inspection_id' | 'released_on'>,
    visits: Pick<InspectionVisit, 'id' | 'kind'>[],
): string | null {
    if (current.condition === 'held') return null;
    const day = shortDay(current.released_on);
    if (!day) return null;
    const kind = visits.find(v => v.id === current.released_inspection_id)?.kind;
    return kind === 'recheck' ? `Re-checked and released ${day}.` : `Released ${day}.`;
}

/** The newest visit that carries angle shots. A re-check takes none, so the newest visit may have none to open. */
export function newestVisitWithAngleShots(visits: Pick<InspectionVisit, 'id' | 'photos'>[]): number | null {
    return visits.find(v => v.photos.some(p => p.slot !== null))?.id ?? null;
}

/** 'December round · window 1–7 Dec', or null before the first round. */
export function roundLabel(current: Pick<InspectionCurrent, 'period' | 'opens_on' | 'closes_on'>): string | null {
    const round = monthName(current.period);
    const range = windowRange(current.opens_on, current.closes_on);
    if (!round) return null;
    return range ? `${round} round · window ${range}` : `${round} round`;
}

/** The order a visit's non-OK lines are listed in: what holds the car first. */
const GRADE_ORDER: Record<InspectionGrade, number> = {
    fix_now: 0,
    fix_soon: 1,
    fixed: 2,
    not_needed: 3,
    ok: 4,
};

/** A visit's lines split into the ones to read (non-OK, most serious first) and the OK ones. */
export function splitVisitLines(lines: InspectionVisitLine[]): { flagged: InspectionVisitLine[]; ok: InspectionVisitLine[] } {
    const flagged = lines
        .filter(l => l.grade !== 'ok')
        .map((line, position) => ({ line, position }))
        .sort((a, b) => GRADE_ORDER[a.line.grade] - GRADE_ORDER[b.line.grade] || a.position - b.position)
        .map(x => x.line);
    return { flagged, ok: lines.filter(l => l.grade === 'ok') };
}

/**
 * A line's grade as the inspector chose it (plan 3.4, and the capture screen's words): an open line
 * graded again reads "Still must fix", "Down to fix soon", "Still fix soon" or "Now must fix".
 * Every other line reads its plain grade, as the driver app's certificate does.
 */
export function lineGradeLabel(line: Pick<InspectionVisitLine, 'grade' | 'previous_grade'>): string {
    const { grade, previous_grade: previous } = line;
    if (grade === 'fix_now') {
        if (previous === 'fix_now') return 'Still must fix';
        if (previous === 'fix_soon') return 'Now must fix';
    }
    if (grade === 'fix_soon') {
        if (previous === 'fix_soon') return 'Still fix soon';
        if (previous === 'fix_now') return 'Down to fix soon';
    }
    return GRADE_LABELS[grade];
}

/**
 * '2nd inspection' for an item carried once, and so on (plan 6 step 5 and section 9: "open since"
 * and "2nd inspection"); null when the item was raised at the latest visit.
 */
export function carriedLabel(timesCarried: number): string | null {
    if (!Number.isSafeInteger(timesCarried) || timesCarried <= 0) return null;
    const n = timesCarried + 1;
    const tens = n % 100;
    const suffix = tens >= 11 && tens <= 13 ? 'th' : n % 10 === 1 ? 'st' : n % 10 === 2 ? 'nd' : n % 10 === 3 ? 'rd' : 'th';
    return `${n}${suffix} inspection`;
}
